import { Injectable } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {AppSettings} from '../../app.settings';

export interface BrandThemeOverride {
    ourlyPrimaryColor: string;
    ourlySecondaryColor: string;
    wallitTertiaryColor: string;
    wallitGradientSWColor: string;
    wallitGradientNEColor: string;
}

@Injectable({
    providedIn: 'root'
})
export class BrandsService {

    private isBranded = false;

    private brand;
    private allBrands: any[] = [];
    private companyThemeOverride: Partial<BrandThemeOverride> | null = null;

    private defaultBrand = {
        name: 'Wallit',
        tagline: 'The Workplace Wallet',
        logoUrl: 'assets/logo/Wallit - Full Logo.svg',
        ourlyPrimaryColor: '#FF224A',
        ourlySecondaryColor: '#171A51',
        wallitTertiaryColor: '#3AE3A0',
        wallitGradientSWColor: '#FF005D',
        wallitGradientNEColor: '#FF4139',
        lightLogoUrl: '/assets/logo/Wallit - Brandmark.svg',
        verticalLogoOnWhiteUrl: 'assets/logo/Wallit - Full Logo - White.png',
        horizontalLogoUrl: '/assets/logo/Wallit - Horizontal - White All.png',
        horizontalLogoOnWhiteUrl: '/assets/logo/Wallit - Horizontal.svg',
        configjson: { bands: [], content: {ui: {giftcards: true, products: true}} },
        redirectUrl: 'wallit.app'
    };

    constructor(
        private httpClient: HttpClient,
    ) {
        this.brand = this.defaultBrand;
    }

    getBrand(): any {
        return this.brand;
    }

    init(): Promise<any> {
        const subdomain = this.getSubdomain();
        return this.httpClient.get(AppSettings.getEndpoint() + 'public/partners').toPromise().then(data => {
            this.allBrands = Array.isArray(data) ? data : [];
            const ourBrand = this.resolveBrandBySlug(subdomain);
            const wallitBrandInfo = this.allBrands.find((brand: { name: string; }) => brand.name === 'Wallit');
            if (wallitBrandInfo) {
                this.defaultBrand.configjson = this.parseConfigJson(wallitBrandInfo.configjson);
            }
            this.setBrand(subdomain ? ourBrand : undefined);
        });
    }

    setPayrollAdminUserBrandFallback(userBrand: any): boolean {
        if (!AppSettings.isPayrollAdmin() || this.hasExplicitBrandContext() || !userBrand) {
            return false;
        }

        const brand = this.resolveBrandFromUserBrand(userBrand);
        if (!brand) {
            return false;
        }

        this.setBrand(brand);
        return true;
    }

    hasExplicitBrandContext(location: Pick<Location, 'host' | 'hostname'> = window.location): boolean {
        return !!this.getSubdomain(location);
    }

    getPrimaryColor(): string {
        return this.getEffectiveBrandTheme().ourlyPrimaryColor;
    }

    getCampaignBrand(): string {
        return this.brand.redirectUrl.split('.')[0];
    }

    getSubdomain(location: Pick<Location, 'host' | 'hostname'> = window.location): string {
        const overrideBrand = AppSettings.getBrandOverride();
        if (overrideBrand) {
            return overrideBrand.trim().toLowerCase();
        }

        if (AppSettings.isPayrollAdmin()) {
            return this.getPayrollAdminHostnameBrand(location.hostname || location.host || '');
        }

        return (location.host || '').split('.')[0].toLowerCase();
    }

    getBrandSubdomain(): string {
        return this.isBranded ? this.getBrandSlug(this.brand) || this.getSubdomain() : '';
    }

    getVerticalLogoOnWhite(): string {
        return this.brand.verticalLogoOnWhiteUrl ? this.brand.verticalLogoOnWhiteUrl : this.brand.logoUrl;
    }

    getHorizontalLogo(): string {
        return this.brand.horizontalLogoUrl ? this.brand.horizontalLogoUrl : this.brand.lightLogoUrl;
    }

    getHorizontalLogoOnWhite(): string {
        return this.brand.horizontalLogoOnWhiteUrl ? this.brand.horizontalLogoOnWhiteUrl : this.brand.logoUrl;
    }

    getLogoOnWhite(): string {
        return this.brand.logoUrl;
    }

    getWallitIcon(): string {
        return this.brand.lightLogoUrl;
    }

    getLearnMore(): Array<any> {
        return this.getConfigJSON().learnMore;
    }

    getContentMessage(id: string): string {
        return this.getConfigJSON().content.messages[id];
    }

    getName(): string {
        return this.brand.name;
    }

    getTagline(): string {
        return this.brand.tagline;
    }

    getConfigJSON(): any {
        return this.brand.configjson;
    }

    getUIConfig(): any {
        return Object.assign(this.getConfigJSON().content.ui, {oldUi: false});
    }

    setCompanyThemeOverride(theme: Partial<BrandThemeOverride> | null | undefined): void {
        const normalizedTheme = theme ? {
            ourlyPrimaryColor: `${theme.ourlyPrimaryColor || ''}`.trim(),
            ourlySecondaryColor: `${theme.ourlySecondaryColor || ''}`.trim(),
            wallitTertiaryColor: `${theme.wallitTertiaryColor || ''}`.trim(),
            wallitGradientSWColor: `${theme.wallitGradientSWColor || ''}`.trim(),
            wallitGradientNEColor: `${theme.wallitGradientNEColor || ''}`.trim(),
        } : null;
        const hasCompleteTheme = !!(
            normalizedTheme?.ourlyPrimaryColor &&
            normalizedTheme?.ourlySecondaryColor &&
            normalizedTheme?.wallitTertiaryColor &&
            normalizedTheme?.wallitGradientSWColor &&
            normalizedTheme?.wallitGradientNEColor
        );

        this.companyThemeOverride = hasCompleteTheme ? normalizedTheme : null;
        this.applyBrandStyles();
    }

    clearCompanyThemeOverride(): void {
        this.companyThemeOverride = null;
        this.applyBrandStyles();
    }

    private setBrand(brand: any): void {
        if (brand) {
            this.isBranded = true;
            this.brand = this.normalizeBrand(brand);
        } else {
            this.isBranded = false;
            this.brand = this.defaultBrand;
        }
        this.applyBrandStyles();
    }

    private getPayrollAdminHostnameBrand(hostname: string): string {
        const normalizedHostname = `${hostname || ''}`.toLowerCase();
        const labels = normalizedHostname.split('.').filter(Boolean);
        const payrollAdminIndex = labels.indexOf('payrolladmin');
        if (payrollAdminIndex > 0) {
            return labels[0];
        }
        if (payrollAdminIndex === 0 || this.isGenericPayrollAdminHost(labels[0])) {
            return '';
        }
        return labels[0] || '';
    }

    private isGenericPayrollAdminHost(firstLabel: string): boolean {
        return ['localhost', '127', 'teamadmin', 'payrolladmin', 'wallit', 'qa', 'demo', 'staging', 'prod', 'dev', 'my']
            .includes(firstLabel);
    }

    private resolveBrandBySlug(slug: string): any {
        if (!slug) {
            return undefined;
        }
        return this.allBrands.find((brand: any) => {
            const brandSlug = this.getBrandSlug(brand);
            return brandSlug && brandSlug.includes(slug);
        });
    }

    private resolveBrandFromUserBrand(userBrand: any): any {
        const partnerId = `${userBrand?.partnerId || ''}`.trim();
        const slug = this.getBrandSlug(userBrand);
        const matchingBrand = this.allBrands.find((brand: any) => {
            return (partnerId && `${brand?.partnerId || ''}`.trim() === partnerId)
                || (slug && this.getBrandSlug(brand) === slug);
        });

        return matchingBrand || userBrand;
    }

    private getBrandSlug(brand: any): string {
        const redirectUrl = `${brand?.redirectUrl || ''}`.trim().toLowerCase();
        if (redirectUrl) {
            return redirectUrl.replace(/^https?:\/\//, '').split('.')[0];
        }
        return `${brand?.brand || brand?.shortName || ''}`.trim().toLowerCase();
    }

    private normalizeBrand(brand: any): any {
        return {
            ...this.defaultBrand,
            ...brand,
            configjson: this.parseConfigJson(brand?.configjson),
            horizontalLogoOnWhiteUrl: brand?.horizontalLogoOnWhiteUrl || brand?.horizontalLogoOnWhite || brand?.logoUrl || this.defaultBrand.logoUrl,
            redirectUrl: brand?.redirectUrl || this.defaultBrand.redirectUrl,
        };
    }

    private parseConfigJson(configjson: any): any {
        if (typeof configjson !== 'string') {
            return configjson || this.defaultBrand.configjson;
        }
        try {
            return JSON.parse(configjson);
        } catch (_) {
            return this.defaultBrand.configjson;
        }
    }

    private applyBrandStyles(): void {
        const effectiveTheme = this.getEffectiveBrandTheme();
        const styles = document.documentElement.style;
        styles.setProperty('--app-primary', effectiveTheme.ourlyPrimaryColor);
        styles.setProperty('--app-secondary', effectiveTheme.ourlySecondaryColor);
        styles.setProperty('--app-primary-rgb', this.hexToRgb(effectiveTheme.ourlyPrimaryColor));
        styles.setProperty('--app-secondary-rgb', this.hexToRgb(effectiveTheme.ourlySecondaryColor));

        styles.setProperty('--app-color-tertiary', effectiveTheme.wallitTertiaryColor);
        styles.setProperty('--app-color-gradientsw', effectiveTheme.wallitGradientSWColor);
        styles.setProperty('--app-color-gradientne', effectiveTheme.wallitGradientNEColor);
        styles.setProperty('--app-color-default-primary', this.defaultBrand.ourlyPrimaryColor);
        styles.setProperty('--app-color-default-secondary', this.defaultBrand.ourlySecondaryColor);
        styles.setProperty('--app-color-default-tertiary', this.defaultBrand.wallitTertiaryColor);
        styles.setProperty('--app-color-default-gradientsw', this.defaultBrand.wallitGradientSWColor);
        styles.setProperty('--app-color-default-gradientne', this.defaultBrand.wallitGradientNEColor);
        this.syncIntercomTheme();
    }

    private getEffectiveBrandTheme(): BrandThemeOverride {
        return {
            ourlyPrimaryColor: this.companyThemeOverride?.ourlyPrimaryColor || this.brand.ourlyPrimaryColor,
            ourlySecondaryColor: this.companyThemeOverride?.ourlySecondaryColor || this.brand.ourlySecondaryColor,
            wallitTertiaryColor: this.companyThemeOverride?.wallitTertiaryColor || this.brand.wallitTertiaryColor,
            wallitGradientSWColor: this.companyThemeOverride?.wallitGradientSWColor || this.brand.wallitGradientSWColor,
            wallitGradientNEColor: this.companyThemeOverride?.wallitGradientNEColor || this.brand.wallitGradientNEColor,
        };
    }

    private syncIntercomTheme(): void {
        const intercomSettings = (window as any).intercomSettings || {};
        const primaryColor = this.getEffectiveBrandTheme().ourlyPrimaryColor || this.defaultBrand.ourlyPrimaryColor;
        const updatedIntercomSettings = {
            ...intercomSettings,
            action_color: primaryColor,
            background_color: primaryColor
        };

        (window as any).intercomSettings = updatedIntercomSettings;

        const intercom = (window as any).Intercom;
        if (typeof intercom === 'function') {
            intercom('update', updatedIntercomSettings);
        }
    }

    private hexToRgb(color: string): string {
        const normalized = String(color || '').trim().replace('#', '');
        if (!normalized) {
            return '0, 0, 0';
        }

        const expanded = normalized.length === 3
            ? normalized.split('').map((char) => `${char}${char}`).join('')
            : normalized;

        if (expanded.length !== 6) {
            return '0, 0, 0';
        }

        const red = Number.parseInt(expanded.slice(0, 2), 16);
        const green = Number.parseInt(expanded.slice(2, 4), 16);
        const blue = Number.parseInt(expanded.slice(4, 6), 16);

        if ([red, green, blue].some((value) => Number.isNaN(value))) {
            return '0, 0, 0';
        }

        return `${red}, ${green}, ${blue}`;
    }

}
